import React, { useEffect, useState } from 'react';
import { Box, Page, Divider } from '@wix/design-system';
import s from '../pages.scss';
import { CenteredLoader } from '../../components/centered-loader';
import { FetcherDecorator } from '../../decorators/fetcher-decorator';
import {
  appMarketDemoLinkClicked,
  appMarketDeveloperContactBoxActionClick,
  appMarketPageView,
  appMarketAppPageScrollPage,
} from '@wix/bi-logger-market/v2';
import { getAppPageDataById, getAppPageDataBySlug } from './app-page-api';
import type { SupportedAppStatuses } from '../../types/app';
import {
  useEssentials,
  useJunkYard,
  useSiteContext,
  useServices,
} from '../../contexts';
import { AppNotification } from './app-page-sections/notifications/app-notification';
import { ReviewsSection } from './app-page-sections/reviews/reviews-section';
import {
  AppPageSticky,
  QuickInfo,
  Breadcrumbs,
  CompanyInfo,
  AppOverview,
  VisibilityDetector,
  AppType,
} from '@wix/app-market-components';
import { AppProperties } from './app-page-sections/app-properties';
import { AppPageCTA } from './app-page-cta';
import { useQuickInfoActions } from './app-page-sections/quick-info/quick-info-actions';
import type { getAppPageDataResponse } from './app-page-api';
import { AppPricingPlansSection } from './app-page-sections/pricing-plans';
import { getAppPageViewBiData } from '../../bi-services/events-data-parser';
import { AppMarketPage } from '../../enums';
import type { getReviewsSectionResponse } from './app-page-sections/reviews/reviews-api';
import { RecommendedForYouSection } from './app-page-sections/recommended-for-you';

export interface AppPageProps {
  id?: string;
  slug?: string;
  status?: SupportedAppStatuses;
}

export interface AppPageComponentProps {
  app: getAppPageDataResponse['app'];
  notification: getAppPageDataResponse['notification'];
  overview: getAppPageDataResponse['overview'];
  reviews: getAppPageDataResponse['reviews'];
  quickInfo: getAppPageDataResponse['quickInfo'];
  companyInfo: getAppPageDataResponse['companyInfo'];
  properties: getAppPageDataResponse['properties'];
  onChange: (reviews: getAppPageDataResponse['reviews']) => void;
  pricingPlans: getAppPageDataResponse['pricingPlans'];
}

export function AppPageComponent({
  app,
  notification,
  reviews,
  overview,
  quickInfo,
  properties,
  companyInfo,
  onChange,
  pricingPlans,
}: AppPageComponentProps) {
  const {
    onCompanyClick,
    onGalleryChange,
    onBack: onBackAction,
    onTagClick,
    onReviewsClick,
  } = useQuickInfoActions();
  const { routerData } = useJunkYard();
  const { metaSiteId, isPremium } = useSiteContext();
  const { i18n, biLogger, experiments, router } = useEssentials();
  const [shouldShowHeader, setShouldShowHeader] = useState<boolean>(false);
  const onBack = router.hasHistory() ? () => onBackAction(app.id) : undefined;

  useEffect(() => {
    const urlParams = location.pathname.split('/')[3];
    const searchTerm = location.search.split('referralTag=')?.[1];
    biLogger.report(
      appMarketPageView(
        getAppPageViewBiData({
          routerData,
          urlParams,
          pageType: AppMarketPage.APP_PAGE,
          appId: app.id,
          appSlug: app.slug,
          appReviewCount: reviews.reviewsSummary.totalReviews,
          appReviewScore: reviews.reviewsSummary.averageRating
            ? Number(reviews.reviewsSummary.averageRating.toFixed(2)) * 100
            : -1000,
          isAppDependency: properties.appDependencies.length > 0,
          searchTerm,
        }),
      ),
    );
  }, []);

  const onSectionFirstVisible = ({
    sectionName,
    sectionIndex,
  }: {
    sectionName: string;
    sectionIndex: number;
  }) => {
    biLogger.report(
      appMarketAppPageScrollPage({
        app_id: app.id,
        page_part: sectionName,
        page_part_index: sectionIndex,
        tag_name: 'app_page',
      }),
    );
  };

  return (
    <>
      <AppPageSticky
        isVisible={shouldShowHeader}
        icon={app.icon}
        company={{ name: companyInfo.name ?? '' }}
        installation={app.installation}
        bundle={app.bundle}
        pricing={app.pricing}
        onBack={onBack}
        mainActionButton={
          <AppPageCTA
            app={{
              id: app.id,
              isBundle: app.bundle.isBundleApp ?? false,
              isInstalled: app.isInstalled,
              isBuiltIn: !!app.installation.isBuiltIn,
            }}
            notification={notification}
            size="small"
          />
        }
        onCompanyClick={(companySlug: string) =>
          onCompanyClick(companySlug, app.id)
        }
        name={app.name}
        i18n={i18n}
        experiments={experiments}
        isPremiumSite={isPremium}
      />
      <Page className={s.appMarketPage}>
        <Page.Content>
          {notification ? (
            <Box marginTop="12px">
              <AppNotification notification={notification} />
            </Box>
          ) : null}
          <Box marginTop="18px">
            <Breadcrumbs
              onBack={onBack}
              onTagClick={(categorySlug: string, subCategory: string) =>
                onTagClick(categorySlug, subCategory, app.id)
              }
              tags={quickInfo.subCategories}
              i18n={i18n}
            />
          </Box>
          <QuickInfo
            media={quickInfo.media}
            appData={app}
            onCompanyClick={(companySlug: string) =>
              onCompanyClick(companySlug, app.id)
            }
            onReviewsClick={(reviewsAmount: number) =>
              onReviewsClick(app.id, reviewsAmount)
            }
            mainActionButton={
              <VisibilityDetector
                onVisible={() => setShouldShowHeader(false)}
                onHidden={() => setShouldShowHeader(true)}
              >
                <Box align="center">
                  <AppPageCTA
                    app={{
                      id: app.id,
                      isBundle: app.bundle.isBundleApp ?? false,
                      isInstalled: app.isInstalled,
                      isBuiltIn: !!app.installation.isBuiltIn,
                    }}
                    notification={notification}
                  />
                </Box>
              </VisibilityDetector>
            }
            onGalleryChange={(oldIndex: number, newIndex: number) =>
              onGalleryChange(oldIndex, newIndex, app.id)
            }
            i18n={i18n}
            experiments={experiments}
            isPremiumSite={isPremium}
          />

          <Box margin="36px 0px">
            <Divider />
          </Box>

          <VisibilityDetector
            onFirstVisible={() =>
              onSectionFirstVisible({
                sectionName: 'SolutionOverview',
                sectionIndex: 1,
              })
            }
          >
            <Box gap="24px">
              <Box flex={1}>
                <AppOverview
                  appName={app.name}
                  description={overview.description}
                  benefits={overview.benefits}
                  demoUrl={overview.demoUrl}
                  onClickDemoUrl={() => {
                    biLogger.report(
                      appMarketDemoLinkClicked({
                        app_id: app.id,
                        demo_link: overview.demoUrl,
                      }),
                    );
                  }}
                  i18n={i18n}
                />
              </Box>
              <Box direction="vertical" gap="24px">
                <AppProperties
                  appId={app.id}
                  appName={app.name}
                  isWixApp={app.type === AppType.WIX_APP}
                  geoAvailability={properties.geoAvailability}
                  appCollections={properties.appCollections}
                  supportedLanguages={properties.supportedLanguages}
                  isAppContentTranslatable={properties.isAppContentTranslatable}
                  appDependencies={properties.appDependencies}
                />
                <CompanyInfo
                  appInfo={{
                    name: companyInfo.name,
                    imageURL: companyInfo.image,
                  }}
                  website={{
                    href: companyInfo.websiteUrl,
                    onClick: () => {
                      biLogger.report(
                        appMarketDeveloperContactBoxActionClick({
                          app_id: app.id,
                          actionName: 'visit_website',
                        }),
                      );
                    },
                  }}
                  contactSupport={{
                    href: companyInfo.contactUs,
                    onClick: () => {
                      biLogger.report(
                        appMarketDeveloperContactBoxActionClick({
                          app_id: app.id,
                          actionName: 'contact_support',
                        }),
                      );
                    },
                  }}
                  privacyPolicy={{
                    href: companyInfo.privacyPolicyUrl,
                    onClick: () => {
                      biLogger.report(
                        appMarketDeveloperContactBoxActionClick({
                          app_id: app.id,
                          actionName: 'privacy',
                        }),
                      );
                    },
                  }}
                  i18n={i18n}
                />
              </Box>
            </Box>
          </VisibilityDetector>

          <Box margin="36px 0px">
            <Divider />
          </Box>

          <VisibilityDetector
            onFirstVisible={() =>
              onSectionFirstVisible({
                sectionName: 'SolutionReviews',
                sectionIndex: 2,
              })
            }
          >
            <ReviewsSection
              app={app}
              company={companyInfo}
              reviews={reviews}
              onChange={onChange}
            />
          </VisibilityDetector>

          {!(pricingPlans.isFreeApp || app.type === AppType.WIX_APP) && (
            <>
              <Box margin="36px 0px">
                <Divider />
              </Box>

              <VisibilityDetector
                onFirstVisible={() =>
                  onSectionFirstVisible({
                    sectionName: 'SolutionPricingPlans',
                    sectionIndex: 3,
                  })
                }
              >
                <AppPricingPlansSection {...pricingPlans} />
              </VisibilityDetector>
            </>
          )}

          {metaSiteId && (
            <>
              <Box margin="36px 0px">
                <Divider />
              </Box>
              <VisibilityDetector
                onFirstVisible={() =>
                  onSectionFirstVisible({
                    sectionName: 'SolutionRelatedApps',
                    sectionIndex: 4,
                  })
                }
              >
                <RecommendedForYouSection appId={app.id} />
              </VisibilityDetector>
            </>
          )}
        </Page.Content>
      </Page>
    </>
  );
}

export function AppPage({ id, slug, status = 'PUBLISHED' }: AppPageProps) {
  const { httpClient, rpcClient, i18n, experiments } = useEssentials();
  const siteContext = useSiteContext();
  const services = useServices();
  const languageCode = i18n.language;
  const [reviews, setReviews] = useState<
    getReviewsSectionResponse | undefined
  >();

  return (
    <FetcherDecorator
      initialFetch={() =>
        id
          ? getAppPageDataById({
              httpClient,
              rpcClient,
              services,
              id,
              languageCode,
              siteContext,
              status,
              experiments,
            })
          : slug
          ? getAppPageDataBySlug({
              httpClient,
              rpcClient,
              services,
              slug,
              languageCode,
              status,
              siteContext,
              experiments,
            })
          : Promise.reject(new Error('No id or slug provided'))
      }
      loader={<CenteredLoader />}
      memoId={`app-page-${id || slug}-${languageCode}-${status}`}
    >
      {({ data }) => {
        return (
          <AppPageComponent
            {...data}
            reviews={reviews ?? data.reviews}
            onChange={(newReviews: getReviewsSectionResponse) => {
              setReviews(newReviews);
            }}
          />
        );
      }}
    </FetcherDecorator>
  );
}
